import React from 'react';
import { makeStyles } from '@material-ui/core';

import { Grid, units } from '@ha/dsl';

import {
  DATEPICKER_VARIANT,
  FOCUSED_FIELD,
  DateRange,
  DateRangePickerLazy,
} from '@ha/marketplace/feature-date-picker';
import { normalizeDate } from '@ha/date';
import { useIntl } from 'ha/i18n';
import { DateFilterValue } from 'ha/types/SearchFilters';
import constants from 'ha/constants/commonConstants';
import { useMediaQuery } from 'ha/helpers/MediaQuery';

import SearchDateRangePickerInputs from './SearchDateRangePickerInputs';

interface Props {
  value: DateFilterValue;
  onChange: (value: DateFilterValue) => void;
}

const useStyles = makeStyles(() => ({
  container: {
    paddingBottom: units.rem(0.875),
  },
}));

const SearchDateRangePicker: React.FC<Props> = ({ value, onChange }) => {
  const classes = useStyles();
  const { T, formatDate: intlFormatDate, locale } = useIntl();
  const { sm } = useMediaQuery();

  const [localValue, setLocalValue] = React.useState(value);

  const [isLoaded, setIsLoaded] = React.useState(false);
  const [focusedDummyField, setFocusedDummyField] = React.useState<
    FOCUSED_FIELD | undefined
  >(undefined);

  React.useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const datePickerValue: DateRange = React.useMemo(
    () => ({
      from: localValue.startDate ? new Date(localValue.startDate) : undefined,
      to: localValue.endDate ? new Date(localValue.endDate) : undefined,
    }),
    [localValue],
  );

  const handleDateChange = React.useCallback((inputValue: DateRange) => {
    setLocalValue({
      startDate: normalizeDate(inputValue.from) || null,
      endDate: normalizeDate(inputValue.to) || null,
    });
  }, []);

  const handleBlur = React.useCallback(() => {
    if (localValue === value) {
      return;
    }

    onChange(localValue);
  }, [onChange, localValue, value]);

  const inputProps = React.useMemo(
    () => ({ placeholder: T('Select'), InputLabelProps: { shrink: true } }),
    [T],
  );
  const fromInputProps = React.useMemo(
    () => ({
      ...inputProps,
      inputRef: (element: HTMLElement) => {
        if (focusedDummyField === FOCUSED_FIELD.FROM && element) {
          element.click();
        }
      },
    }),
    [inputProps, focusedDummyField],
  );
  const toInputProps = React.useMemo(
    () => ({
      ...inputProps,
      inputRef: (element: HTMLElement) => {
        if (focusedDummyField === FOCUSED_FIELD.TO && element) {
          element.click();
        }
      },
    }),
    [inputProps, focusedDummyField],
  );

  const dummyFromInputValue = localValue.startDate
    ? intlFormatDate('DD MMM YYYY', localValue.startDate)
    : '';
  const dummyToInputValue = localValue.endDate
    ? intlFormatDate('DD MMM YYYY', localValue.endDate)
    : '';
  const loadDatePicker = React.useCallback(async () => {
    await DateRangePickerLazy.load();

    setIsLoaded(true);
  }, []);

  const handleDummyFromInputFocus = React.useCallback(async () => {
    setFocusedDummyField(FOCUSED_FIELD.FROM);

    await loadDatePicker();
  }, [loadDatePicker]);
  const handleDummyToInputFocus = React.useCallback(async () => {
    setFocusedDummyField(FOCUSED_FIELD.TO);

    await loadDatePicker();
  }, [loadDatePicker]);
  const handleDummyInputBlur = React.useCallback(() => {
    setFocusedDummyField(undefined);
  }, []);
  const dummyFromInputProps = React.useMemo(
    () => ({
      ...inputProps,
      label: T('Move-in'),
      onBlur: handleDummyInputBlur,
      'data-test-locator': 'DateRangePicker / From',
      id: 'date-filter',
    }),
    [T, inputProps, handleDummyInputBlur],
  );
  const dummyToInputProps = React.useMemo(
    () => ({
      ...inputProps,
      label: T('Move-out'),
      onBlur: handleDummyInputBlur,
      'data-test-locator': 'DateRangePicker / To',
    }),
    [T, inputProps, handleDummyInputBlur],
  );

  return isLoaded ? (
    <DateRangePickerLazy
      value={datePickerValue}
      onDateChange={handleDateChange}
      onBlur={handleBlur}
      DatePickerInputs={SearchDateRangePickerInputs}
      variant={DATEPICKER_VARIANT.INLINE}
      clearDatesButton={T('cta.clear_dates')}
      fromEmptyValueLabel=""
      toEmptyValueLabel=""
      fromInputLabel={T('Move-in')}
      toInputLabel={T('Move-out')}
      fromInputProps={fromInputProps}
      toInputProps={toInputProps}
      noEndDate
      showSingleMonth={!sm}
      T={T}
      formatDate={intlFormatDate}
      locale={locale}
      listingFlexibleDays={constants.listingFlexibleDays}
    />
  ) : (
    <Grid container className={classes.container}>
      <SearchDateRangePickerInputs
        from={dummyFromInputValue}
        to={dummyToInputValue}
        fromInputProps={dummyFromInputProps}
        toInputProps={dummyToInputProps}
        onFromFocus={handleDummyFromInputFocus}
        onToFocus={handleDummyToInputFocus}
        isFromFocused={focusedDummyField === FOCUSED_FIELD.FROM}
        isToFocused={focusedDummyField === FOCUSED_FIELD.TO}
      />
    </Grid>
  );
};

export default SearchDateRangePicker;
